<template>
	<div class="balance_resumen_tab report_theme_container" data-theme="resultados">
		<TabHeader
			title="Balance"
			description="Visualiza la posición financiera de tu negocio en un solo lugar."
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			:compact="true"
			calendar-mode="range"
			:date-range="calendarRange"
			:year-range="yearRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange" />

		<!-- Table Section -->
		<div class="underHeader">
			<FinancialResultsTable
				class="mt-4"
				:rows="tableRows"
				:headers="monthHeaders"
				:isLoading="isLoading"
				:filterPeriod="filterPeriod"
				:expandedRows="expandedRowIds"
				:show-period-select="false"
				@toggle-childs="toggleL2" />
		</div>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapActions, mapMutations } from "vuex";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { displayMonthRange, displayYearRange } from "@/store/financialReports/utils/dateRange";

import TabHeader from "@/components/financialReports/TabHeader.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";

// Realce de cada banda de nivel 1: Activos en azure (cian), Pasivos y Patrimonio
// en blue — mismo lenguaje de color que la imagen de referencia. Los tokens
// theme_row_* viven en FinancialResultsTable.
const L1_THEME = {
	Activos: "theme_row_azure",
	Pasivos: "theme_row_blue",
	Patrimonio: "theme_row_blue",
};

export default {
	name: "BalanceResumenTab",
	components: {
		TabHeader,
		FinancialResultsTable
	},
	data() {
		return {
			filterOptions: FILTER_PERIOD_OPTIONS,
			// Ids de las filas L2 con su nivel 3 desplegado. Arranca vacío → L3
			// plegado por defecto (sólo se ven L1 + L2 al abrir la vista).
			expandedRowIds: [],
		};
	},
	computed: {
		...mapState("reportsFilters", ["dataFirstMonth", "endMonth", "selectedPeriodRange", "customRange", "customYearRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("balanceReport", ["groups", "accountsData", "monthHeaders", "isLoading"]),
		...mapState("userAuth", ["user", "company"]),

		// Sin mayor (empresa del clasificador/SII) el Resumen muestra sólo
		// Activos: Pasivos y Patrimonio son flujos del período sin aperturas ni
		// saldo contable, y la identidad A = P + Pat no aplica (decisión
		// 2026-09-10, "por ahora"; el sidebar y los tabs siguen el mismo gate).
		hasLedger() {
			return Boolean(this.company && this.company.has_ledger);
		},
		visibleGroups() {
			if (this.hasLedger) return this.groups;
			return this.groups.filter((group) => group.level1 === "Activos");
		},

		// Fila de validación contable: sólo usuarios internos (analistas
		// @cforemoto.com). Gate cosmético — el dato es derivable a mano de la
		// tabla visible; no es información sensible, es una herramienta.
		isInternalUser() {
			return /@cforemoto\.com$/i.test((this.user && this.user.email) || "");
		},
		// Filas del árbol Balance, en orden: por cada grupo de nivel 1 (Activos →
		// Pasivos → Patrimonio) una fila total coloreada (suma de sus L2), luego
		// sus L2 (siempre visibles), y bajo cada L2 sus cuentas L3 sólo cuando
		// esa L2 está expandida. Sólo las L2 con hijos L3 reales llevan chevron.
		tableRows() {
			const rows = [];
			this.visibleGroups.forEach((group) => {
				const l2List = group.level2Accounts;
				rows.push({
					id: `l1:${group.level1}`,
					label: group.level1,
					theme: L1_THEME[group.level1] || "theme_row_blue",
					isMoney: true,
					itemsDict: this.sumItems(l2List),
				});

				l2List.forEach((l2) => {
					const childrenL3 = (group.hierarchy[l2] || []).filter((l3) => l3 !== l2);
					const l2RowId = `l2:${l2}`;
					rows.push({
						id: l2RowId,
						label: l2,
						theme: "theme_row_white",
						isMoney: true,
						itemsDict: this.accountsData[l2] || {},
						hasChilds: childrenL3.length > 0,
					});

					if (this.expandedRowIds.includes(l2RowId)) {
						childrenL3.forEach((l3) => {
							rows.push({
								id: `l3:${l2}:${l3}`,
								label: l3,
								theme: "theme_row_detail_bills",
								isMoney: true,
								itemsDict: this.accountsData[l3] || {},
							});
						});
					}
				});
			});
			if (this.isInternalUser && this.hasLedger && this.groups.length) {
				rows.push(this.buildValidationRow());
			}
			return rows;
		},
		getNow() {
			return moment().toDate();
		},
		// Rango activo del calendar del header (modo range), idéntico a Resultados:
		// customRange si existe, si no el default de últimos 12 meses sobre endMonth.
		calendarRange() {
			return displayMonthRange(this.endMonth, this.customRange, this.dataFirstMonth);
		},
		yearRange() {
			return displayYearRange(this.endMonth, this.customYearRange, this.dataFirstMonth);
		},
		// Firma reactiva consolidada — un solo trigger para refetch aunque varios
		// filtros cambien en la misma transacción. Mismo criterio que Resultados.
		fetchSignature() {
			return [
				this.filterPeriod,
				this.endMonth,
				this.selectedPeriodRange,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},
	},
	watch: {
		fetchSignature() {
			this.fetchBalanceData();
		},
	},
	mounted() {
		this.fetchBalanceData();
	},
	methods: {
		...mapActions("balanceReport", ["fetchBalanceData"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),
		// Suma por cabecera los montos de un conjunto de labels (para el total del
		// nivel 1 = suma de sus L2). El total se calcula, no se lee del backend,
		// así queda consistente con las filas visibles.
		sumItems(labels) {
			const itemsDict = {};
			this.monthHeaders.forEach((header) => {
				itemsDict[header] = labels.reduce((sum, label) => {
					const val = Number(this.accountsData[label]?.[header] || 0);
					return sum + (isNaN(val) ? 0 : val);
				}, 0);
			});
			return itemsDict;
		},
		// Fila de control para analistas: la identidad contable intra-año
		// A = P + Pat + Resultado del ejercicio, evaluada sobre los MISMOS
		// totales de banda que muestra la tabla — el Patrimonio renderizado YA
		// incluye la "Utilidad Antes de Impuestos" (inyectada por el backend),
		// así que la lectura operativa es A − (P + Pat) = 0. Un residuo ≠ 0 es
		// un descuadre de data (aperturas faltantes, docs sin clasificar,
		// prorrateos ≠ 100%, cuentas mal mapeadas). Redondeado a peso y con
		// `|| 0` para normalizar el -0 de Math.round sobre ruido de float.
		buildValidationRow() {
			const totalsByLevel1 = {};
			this.groups.forEach((group) => {
				totalsByLevel1[group.level1] = this.sumItems(group.level2Accounts);
			});
			const itemsDict = {};
			this.monthHeaders.forEach((header) => {
				const total = (level1) => Number(totalsByLevel1[level1]?.[header] || 0);
				itemsDict[header] = Math.round(
					total("Activos") - total("Pasivos") - total("Patrimonio"),
				) || 0;
			});
			return {
				id: "validation",
				label: "Validación A − (P + Pat)",
				theme: "theme_row_validation",
				isMoney: true,
				isValidation: true,
				itemsDict,
			};
		},

		toggleL2(rowId) {
			const index = this.expandedRowIds.indexOf(rowId);
			if (index > -1) {
				this.expandedRowIds.splice(index, 1);
			} else {
				this.expandedRowIds.push(rowId);
			}
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		updateYearRange(range) {
			this.setCustomYearRange(range);
		},
	},
};
</script>

<style scoped lang="scss">
.balance_resumen_tab {
	position: relative;
	// Mismo respiro inferior que Resultados para no pegar la card al viewport.
	padding-bottom: 24px;
	height: 100%;
}

// Header común vive en TabHeader.vue.
</style>
